/**
 * Pure H OS · Sprint 6 (integration) · Deep-link + Related-links helpers.
 *
 * Two small non-visual utilities:
 *
 *   • `useDeepLink()`       — reads `?q=` and `?open=` params so any list
 *     page can auto-focus its search input on a term OR auto-open its
 *     detail drawer for a given entity id, powered by URL alone.
 *
 *   • `<RelatedLinksBlock/>` — composed strictly from existing
 *     `lib.overlay` primitives (`ListRow`) + react-router `<Link>`. It is
 *     NOT a new primitive: it's a call site pattern lifted so we don't
 *     copy-paste the same JSX in six drawers.
 */
import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { RelatedLink } from "../mocks/entityLinks";

// ── Deep-link hook ──────────────────────────────────────────────
export function useDeepLink() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const open = params.get("open") ?? "";

  const clearOpen = () => {
    const next = new URLSearchParams(params);
    next.delete("open");
    setParams(next, { replace: true });
  };

  return useMemo(() => ({ q, open, clearOpen }), [q, open]);
}

// ── Auto-open helper (drives a Drawer via URL) ──────────────────
// Wire it inside any list page like:
//   useAutoOpen(open, INVOICES, (i) => i.id, setSelected);
export function useAutoOpen<T>(
  openId: string,
  collection: T[],
  getId: (row: T) => string,
  setSelected: (row: T | null) => void,
) {
  useEffect(() => {
    if (!openId) return;
    const row = collection.find((r) => getId(r) === openId);
    if (row) setSelected(row);
  }, [openId, collection, getId, setSelected]);
}

// ── Related-links block (uses existing primitives only) ─────────
export function RelatedLinksBlock({
  links, title = "Related", testId,
}: { links: RelatedLink[]; title?: string; testId?: string }) {
  if (!links.length) return null;
  return (
    <div className="pt-3" data-testid={testId ?? "related-links"}>
      <div
        className="text-[10px] uppercase tracking-[0.14em] mb-1.5"
        style={{ color: "var(--ph-ink-muted)" }}
      >
        {title}
      </div>
      <div
        className="p-1"
        style={{
          background: "var(--ph-surface)",
          borderRadius: "var(--ph-radius-md)",
          border: "1px solid var(--ph-border-subtle)",
        }}
      >
        {links.map((l) => (
          <Link
            key={l.testId ?? l.href}
            to={l.href}
            data-testid={l.testId ?? `rl-${l.href}`}
            className="flex items-center gap-3 px-3 py-2 transition-colors"
            style={{ borderRadius: "var(--ph-radius-sm)", textDecoration: "none" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ph-hover-bg)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <span
              className="flex items-center justify-center flex-shrink-0"
              style={{ width: 22, height: 22, color: "var(--ph-ink-muted)" }}
            >
              <l.icon size={13} />
            </span>
            <span className="flex-1 min-w-0">
              <span
                className="block text-sm font-medium truncate"
                style={{ color: "var(--ph-ink-strong)" }}
              >
                {l.label}
              </span>
              <span
                className="block text-[10px] uppercase tracking-[0.14em]"
                style={{ color: "var(--ph-ink-muted)" }}
              >
                {l.module}
              </span>
            </span>
            <ArrowUpRight size={13} style={{ color: "var(--ph-ink-soft)" }} />
          </Link>
        ))}
      </div>
    </div>
  );
}
